import { Injectable } from '@angular/core';
import { BaseApiService } from '../base-api.service';
import { HttpClient } from '@angular/common/http';
import { SessionTemplateBasicsDto, SessionTemplateDto } from '../../dto/sessions/session-template.dto';
import { environment } from '@beyou/environments/environment';
import { delay, firstValueFrom, map, Observable } from 'rxjs';
import { SessionTemplate, SessionTemplateBase, SessionTemplateDetails, SessionTemplateTranslation } from '../../models/sessions/session-template.model';
import { SessionTemplateMapper } from '../../mappers/sessions/session-template.mapper';
import { SessionTemplateTranslationsDto } from '../../dto/sessions/session-template-translations.dto';
import { SessionTemplateTranslationMapper } from '../../mappers/sessions/session-template-translation.mapper';
import { SubriseUserContentPreferences } from '../../models/users/subrise-user.model';
import { SessionTemplateBlockInclusionResultDTO, SessionTemplateBlockInclusionTestDTO } from '../../dto/sessions/session-template-block-inclusion-test.dto';
import { UserContentPreferencesMapper } from '../../mappers/users/user-content-preferences.mapper';
import { ExportResultDto } from '../../dto/export-result.dto';
import { ImageAssetUpload } from '../../models/media/cover.model';
import { SessionTemplateQuestionsDto } from '../../dto/sessions/session-template-questions.dto';
import { SessionTemplateQuestions } from '../../models/sessions/session-template-questions.model';
import { SessionTemplateQuestionsMapper } from '../../mappers/sessions/session-template-questions.mapper';

export type SessionTemplateBlocksImportResultDto = { updated: number; not_found: number; skipped: number };

@Injectable({
  providedIn: 'root'
})
export class SessionTemplateApiService extends BaseApiService {

  constructor(http: HttpClient) {
    super(http);
  }

  public getAll(): Observable<SessionTemplate[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates`;
    return this.getAllPagedData<SessionTemplateBasicsDto>(apiUrl).pipe(
      map((response) => {
        return response.map(item => {
          return SessionTemplateMapper.toBasicModel(item);
        })
      })
    );
  }

  public async get(id: number): Promise<SessionTemplateDetails> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}`;
    return await firstValueFrom(this.http.get<SessionTemplateDto>(apiUrl).pipe(
      map((response) => {
        console.debug('Session template retrieved: ', response.id);
        const s = SessionTemplateMapper.toModel(response);
        return { ...s, isDetailed: true };
      })
    ));
  }

  /**
   * The questions to answer when creating a session from this template for `userId`, with the
   * answers they already gave unless `includeAnswers` is false, in one call. `languageId`
   * overrides the user's language.
   */
  public getQuestions(
    id: number,
    userId: string,
    languageId?: number | null,
    includeAnswers: boolean = true,
    personalizationLevel?: string | null
  ): Observable<SessionTemplateQuestions> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}/questions`;
    const params: Record<string, string> = { user_id: userId };
    if (languageId != null) {
      params['language_id'] = String(languageId);
    }
    if (personalizationLevel) {
      params['personalization_level'] = personalizationLevel;
    }
    if (!includeAnswers) {
      params['include_answers'] = 'false';
    }
    return this.http.get<SessionTemplateQuestionsDto>(apiUrl, { params }).pipe(
      map((response) => SessionTemplateQuestionsMapper.toModel(response))
    );
  }

  public async updateStoreStatus(id: number, status: string): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}/store-status`;
    const dto = {
      store_status: status
    }
    return await firstValueFrom(this.http.put(apiUrl, dto, { observe: 'response' }).pipe(
      map((response) => {
        console.debug('Session template store status updated: ', response.status);
        if (response.status === 204) {
          console.debug('No Content - OK');
        } else {
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      })
    ));
  }

  public async update(sessionTemplate: SessionTemplate, coverUpload?: ImageAssetUpload): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${sessionTemplate.id}`;
    const dto = SessionTemplateMapper.toUpdateDto(sessionTemplate, coverUpload);
    return await firstValueFrom(this.http.put(apiUrl, dto, { observe: 'response' }).pipe(
      map((response) => {
        console.debug('session template updated status code: ', response.status);
        if (response.status === 204) {
          console.debug('No Content - OK');
        } else {
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      })
    ));
  }

  public async copy(id: number): Promise<number> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}/copy`;
    const dto = {};
    return await firstValueFrom(this.http.post<number>(apiUrl, dto).pipe(
      map((newId) => {
        console.debug('session template copied: ', newId);
        return newId;
      })
    ));
  }

  public async add(sessionTemplate: SessionTemplateBase, languageId: number, coverUpload?: ImageAssetUpload): Promise<number> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates`;
    const dto = SessionTemplateMapper.toCreateDto(sessionTemplate, languageId, coverUpload);
    console.debug('new session template dto', dto);
    return await firstValueFrom(this.http.post<number>(apiUrl, dto).pipe(
      delay(3000), // Allow time for the cover image to be resized on S3
      map((newId) => {
        console.debug('session template added: ', newId);
        return newId;
      })
    ));
  }

  public async delete(id: number): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}`;
    await firstValueFrom(this.http.delete(apiUrl));
    console.log('Session template deleted: ', id);
  }

  public async getTranslations(id: number): Promise<SessionTemplateTranslation[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}/translations`;
    return await firstValueFrom(this.http.get<SessionTemplateTranslationsDto>(apiUrl).pipe(
      map((response) => {
        console.debug('Session template translations retrieved: ', id);
        return response.translations.map(item => SessionTemplateTranslationMapper.toModel(item));
      })
    ));
  }

  public async updateTranslations(id: number, translations: SessionTemplateTranslation[]): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}/translations`;
    const dto = {
      translations: translations.map(item => SessionTemplateTranslationMapper.toDto(item))
    };
    await firstValueFrom(this.http.post(apiUrl, dto));
    console.debug('Session template translations updated: ', id);
  }

  public async testBlockInclusion(id: number, blockId: number, preferences: SubriseUserContentPreferences): Promise<SessionTemplateBlockInclusionResultDTO> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${id}/test-block-inclusion`;
    const dto: SessionTemplateBlockInclusionTestDTO = {
      template_id: id,
      block_id: blockId,
      user_profile: UserContentPreferencesMapper.toDto(preferences)
    }
    console.debug('block inclusion test dto', dto);
    return await firstValueFrom(this.http.post<SessionTemplateBlockInclusionResultDTO>(apiUrl, dto).pipe(
      map((response) => {
        console.debug('session block inclusion test result: ', response.is_included);
        return response;
      })
    ));
  }

  public async exportBlocksForAllTemplates(languageId: number, blockTypeFilter: string): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/blocks/export?language_id=${languageId}&block_type_filter=${blockTypeFilter}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async exportBlocks(templateId: number, languageId: number, blockTypeFilter: string): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/${templateId}/blocks/export?language_id=${languageId}&block_type_filter=${blockTypeFilter}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async export(languageId: number, archivedFilter: string): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/export?language_id=${languageId}&archived_filter=${archivedFilter}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async import(file: File): Promise<void> {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/import`;
    await firstValueFrom(this.http.post(apiUrl, uploadData));
  }

  /** Update-only: position, pause, repeats and always-include of existing blocks, from the blocks export. */
  public async importBlocks(file: File): Promise<SessionTemplateBlocksImportResultDto> {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const apiUrl = `${environment.adminApiBaseUrl}/session-templates/blocks/import`;
    return await firstValueFrom(this.http.post<SessionTemplateBlocksImportResultDto>(apiUrl, uploadData));
  }

}
